import { useState, type FormEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import type { ApiError } from "../api/client";

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const from = (location.state as { from?: string } | null)?.from ?? "/";

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await login(username.trim(), password);
      navigate(from, { replace: true });
    } catch (err) {
      const e = err as ApiError;
      setError(e.code === "invalid_credentials" ? t("login.invalid") : t("login.errorGeneric"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col items-center justify-center p-6">
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>

      <div className="w-full max-w-sm">
        <h1 className="text-2xl font-semibold mb-1 text-center">{t("login.title")}</h1>
        <p className="text-slate-500 text-sm mb-6 text-center">{t("login.subtitle")}</p>

        <form onSubmit={onSubmit} className="space-y-3">
          <div>
            <label className="block text-xs text-slate-400 mb-1">{t("login.username")}</label>
            <input
              type="text"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-3 py-2 rounded bg-slate-800 border border-slate-700 focus:border-slate-500 outline-none transition duration-200 ease-out"
              required
            />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">{t("login.password")}</label>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 rounded bg-slate-800 border border-slate-700 focus:border-slate-500 outline-none transition duration-200 ease-out"
              required
            />
          </div>

          {error && <div className="text-red-400 text-sm">{error}</div>}

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-2 rounded bg-sky-600 hover:bg-sky-500 disabled:bg-slate-700 disabled:text-slate-500 font-medium transition duration-200 ease-out"
          >
            {submitting ? t("login.submitting") : t("login.submit")}
          </button>
        </form>

        <div className="mt-6 rounded border border-amber-500/40 bg-amber-500/5 p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-amber-300">{t("login.demoTitle")}</span>
            <span className="text-[10px] text-amber-300/60">{t("login.demoHint")}</span>
          </div>
          <div className="space-y-1.5">
            {[
              { u: "admin01", p: "admin1234", role: t("login.demoRoleAdmin") },
              { u: "operator01", p: "operator1234", role: t("login.demoRoleOperator") },
            ].map((acc) => (
              <button
                key={acc.u}
                type="button"
                onClick={() => {
                  setUsername(acc.u);
                  setPassword(acc.p);
                }}
                className="w-full text-left p-2 rounded bg-slate-900/40 hover:bg-slate-900/70 border border-slate-700/50 transition duration-200 ease-out"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-mono text-slate-200">{acc.u} / {acc.p}</span>
                  <span className="text-[11px] text-slate-400">{acc.role}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
